import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useProcessTasks } from '../../api/process-client/process-client.hooks';
import type { GraphData } from '../../api/process-client/process-client.type';
import { useGetWorkflows } from '../../api/workflows-client/workflows.client.hooks';
import { useGlobalStore } from '../../store/global-store/global-store';
import { useProcessStore } from '../../store/process-store/process-store.hooks';
import { filterProcessMapping } from '../process/process.helpers';
import { useSingleViewData } from '../process/process.hooks';
import { useEventGraphEdgeTasks } from './event-graph/event-graph-client.hooks';
import { getWorkflowAndTeamParams } from './process-graph/process-graph.helpers';
import type { ProcessSide } from './process-v2.type';

/**
 * Hook for retrieving process data and task data for both sides of the comparison view
 *
 * @returns {Object} Structured object containing data for left and right sides and shared properties
 */
function useProcessViewData() {
  const organizationId = useGlobalStore((state) => state.organization)?.id || '';
  const portfolioId = useGlobalStore((state) => state.portfolio)?.id || null;
  const {
    workflow: edgeWorkflow,
    stages: edgeStages,
    rightWorkflow: rightEdgeWorkflow,
    rightStages: rightEdgeStages,
    setWorkflow: setEdgeWorkflow,
    setRightWorkflow: setRightEdgeWorkflow,
    isComparisonView,
    eventScope,
    setEventScope,
    rightEventScope,
    setRightEventScope,
    scope,
    setScope,
    rightScope,
    setRightScope,
  } = useProcessStore();

  const { data: workflows } = useGetWorkflows(organizationId, portfolioId);

  // Default empty GraphData object
  const defaultGraphData: GraphData = {
    task_count: 0,
    nodes: [],
    edges: {},
    common_paths: [],
    metrics: {
      backwards_tasks: 0,
      skipped_tasks: 0,
      blocked_tasks: 0,
    },
  };

  // Left side graph data and mapping
  const { paramWorkflow, teamParam } = getWorkflowAndTeamParams(scope, workflows);

  const { mapping, graphData, query } = useSingleViewData({
    team: teamParam,
    board: scope.board,
    taskType: scope.taskType,
    epic: scope.epic,
    workflow: paramWorkflow,
    startDate: scope.startDate,
    endDate: scope.endDate,
    useBloomfilterSections: scope.useGroups,
    task: scope.task,
  });

  // Left side task data
  const { taskData: tasks = [], isFetching: tasksFetching } = useProcessTasks(
    {
      teamId: teamParam?.id ?? null,
      workflowId: paramWorkflow?.id ?? null,
      boardId: scope.board?.id ?? null,
      taskType: scope.taskType,
      epicId: scope.epic?.id ?? null,
      taskId: scope.task?.id ?? null,
      startDate: scope.startDate ? dayjs(scope.startDate).format('YYYY-MM-DD') : undefined,
      endDate: scope.endDate ? dayjs(scope.endDate).format('YYYY-MM-DD') : undefined,
      processWorkflow: edgeWorkflow ?? undefined,
      useGroups: scope.useGroups,
    },
    {
      enabled: Boolean(edgeWorkflow?.mapping),
      staleTime: 1000 * 60 * 5,
    },
  );

  // left side event task data
  const { tasks: eventTasks = [], query: eventQuery } = useEventGraphEdgeTasks({
    teamId: teamParam?.id || '',
    boardId: scope.board?.id || '',
    taskType: scope.taskType || '',
    epicId: scope.epic?.id || '',
    taskId: scope.task?.id || '',
    workflowId: paramWorkflow?.id || '',
    startDate: scope.startDate || '',
    endDate: scope.endDate || '',
    statuses: eventScope.statuses,
    edgeId: eventScope.selectedEdgeId || '',
  });

  // Right side graph data and mapping
  const { paramWorkflow: rightParamWorkflow, teamParam: rightTeamParam } = getWorkflowAndTeamParams(
    rightScope,
    workflows,
  );

  const {
    mapping: rightMapping,
    graphData: rightGraphData,
    query: rightQuery,
  } = useSingleViewData({
    team: rightTeamParam,
    board: rightScope.board,
    taskType: rightScope.taskType,
    epic: rightScope.epic,
    workflow: rightParamWorkflow,
    startDate: rightScope.startDate,
    endDate: rightScope.endDate,
    useBloomfilterSections: rightScope.useGroups,
    task: rightScope.task,
  });

  // Right side task data
  const { taskData: rightTasks = [], isFetching: rightTasksFetching } = useProcessTasks(
    {
      teamId: rightTeamParam?.id ?? null,
      workflowId: rightParamWorkflow?.id ?? null,
      boardId: rightScope.board?.id ?? null,
      taskType: rightScope.taskType,
      epicId: rightScope.epic?.id ?? null,
      taskId: rightScope.task?.id ?? null,
      startDate: rightScope.startDate ? dayjs(rightScope.startDate).format('YYYY-MM-DD') : undefined,
      endDate: rightScope.endDate ? dayjs(rightScope.endDate).format('YYYY-MM-DD') : undefined,
      processWorkflow: rightEdgeWorkflow ?? undefined,
      useGroups: rightScope.useGroups,
    },
    {
      enabled: Boolean(rightEdgeWorkflow?.mapping),
      staleTime: 1000 * 60 * 5,
    },
  );

  // right side event task data
  const { tasks: rightEventTasks = [], query: rightEventQuery } = useEventGraphEdgeTasks({
    teamId: rightTeamParam?.id || '',
    boardId: rightScope.board?.id || '',
    taskType: rightScope.taskType || '',
    epicId: rightScope.epic?.id || '',
    taskId: rightScope.task?.id || '',
    workflowId: rightParamWorkflow?.id || '',
    startDate: rightScope.startDate || '',
    endDate: rightScope.endDate || '',
    statuses: rightEventScope.statuses,
    edgeId: rightEventScope.selectedEdgeId || '',
  });

  // Filter the mappings
  const filteredMapping = useMemo(() => filterProcessMapping(mapping), [mapping]);
  const filteredRightMapping = useMemo(() => filterProcessMapping(rightMapping), [rightMapping]);

  // Construct the left and right side objects
  const leftSide: ProcessSide = {
    graphData: graphData || defaultGraphData,
    mapping: filteredMapping,
    tasks,
    eventTasks,
    isLoading: query.isLoading,
    workflow: edgeWorkflow,
    stages: edgeStages,
    setWorkflow: setEdgeWorkflow,
    scope,
    setScope,
    eventScope,
    setEventScope,
  };

  const rightSide: ProcessSide = {
    graphData: rightGraphData || defaultGraphData,
    mapping: filteredRightMapping,
    tasks: rightTasks,
    eventTasks: rightEventTasks,
    isLoading: rightQuery.isLoading,
    workflow: rightEdgeWorkflow,
    stages: rightEdgeStages,
    setWorkflow: setRightEdgeWorkflow,
    scope: rightScope,
    setScope: setRightScope,
    eventScope: rightEventScope,
    setEventScope: setRightEventScope,
  };

  return {
    left: leftSide,
    right: rightSide,
    isComparisonView,
    isLoading: leftSide.isLoading || rightSide.isLoading,
    isLoadingTasks: tasksFetching || rightTasksFetching || eventQuery.isFetching || rightEventQuery.isFetching,
  };
}

export { useProcessViewData };
